/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * STAMP DEFINITIONS — A HAND-MAINTAINED MIRROR OF THE APP'S `CHALLENGE_BADGES`
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * SOURCE OF TRUTH: `~/shado/components/ProfileChallengesModal.tsx` → `CHALLENGE_BADGES`.
 * Mirrored here on 2026-08-23 against that file. THIS COPY WILL DRIFT — see the note below for why
 * it is still the right call and what the alternative costs.
 *
 * ── WHY A COPY AND NOT A QUERY ───────────────────────────────────────────────────────────────────
 * There were exactly two ways to carry the rules across a repo boundary:
 *
 *   (a) COPY THE TABLE (this file). The dashboard is a separate CRA app with its own tsconfig and
 *       no React Native in its dependency tree, so it cannot import the app module — that module
 *       pulls in `react-native`, `expo`, `@/lib/screenScale` and the app's Supabase client at its
 *       top level. Cost: a stamp added, renamed or re-thresholded in the app is INVISIBLE here
 *       until someone edits this file. Mitigation: the table below is pure DATA — no predicates,
 *       no rendering, no formatting — so a diff against `CHALLENGE_BADGES` is a 9-line eyeball
 *       check, and `STAMP_DEFINITIONS_MIRRORED_ON` dates the last one.
 *
 *   (b) REPRODUCE THE RULES IN SQL (a `SECURITY DEFINER` RPC returning holders per badge). Cost:
 *       a migration + a production DDL deploy for a read-only admin panel, and the thresholds then
 *       live in a THIRD place (app TS, this dashboard, the function body) rather than two. It also
 *       moves the definition somewhere neither repo's diff will show when the app's badge set
 *       changes — strictly worse for drift than (a), not better.
 *
 * (a) was chosen. Note the drift risk is bounded in practice: every criterion in the set is a
 * "distinct count ≥ N" over one of four stats, and that SHAPE has been stable across all three
 * revisions of the badge set (2026-08-01, 08-01 second pass, 08-02). A new stamp on a stat this
 * file does not compute would need a code change here regardless of how the rules travelled.
 *
 * The SQL form of these same rules WAS written and run against production to verify this table —
 * see the verification note in `src/lib/stampHolders.ts`.
 * ──────────────────────────────────────────────────────────────────────────────────────────────── */

/** Last time this table was diffed against the app's `CHALLENGE_BADGES`. Update when you re-check. */
export const STAMP_DEFINITIONS_MIRRORED_ON = '2026-08-23';

/** The four per-user aggregates any stamp criterion may read. Mirrors `ProfileChallengeStats`. */
export type StampStatKey = 'venuesRated' | 'citiesRated' | 'barsRated' | 'clubsRated';

/** One "distinct count ≥ threshold" clause. A stamp is earned when EVERY clause passes. */
export type StampCriterion = { stat: StampStatKey; threshold: number };

export type StampDefinition = {
  /** Same id string the app uses, so the two can be compared without a lookup table. */
  id: string;
  emoji: string;
  title: string;
  /** The app's one-line "how to unlock", verbatim. */
  requirement: string;
  /** 1 = rarest … 9 = easiest. The app's ordering; used here only to sort the tiles. */
  rarity: number;
  /** ANDed. 👑 Elite is the only stamp with two. */
  criteria: StampCriterion[];
};

/**
 * The nine stamps, rarest first — the same order the app's profile row uses.
 *
 * ⚠️ `id` values are historical and DO NOT match their titles: `night_owl` is "Regular",
 * `scene_veteran` is "Night Owl", `out_of_town` is "Traveler", `traveler` is "Frequent Flyer",
 * `worldwide` is "Global Citizen", `disco` is "Clubber", `icon` is "Elite". The app renamed the
 * titles in the 2026-08-01 badge-set revision and deliberately kept the ids (no id is persisted
 * anywhere). Copied as-is so the two files stay diffable.
 */
export const STAMP_DEFINITIONS: StampDefinition[] = [
  {
    id: 'icon',
    emoji: '👑',
    title: 'Elite',
    requirement: 'Rate 50 bars AND 50 clubs.',
    rarity: 1,
    criteria: [
      { stat: 'barsRated', threshold: 50 },
      { stat: 'clubsRated', threshold: 50 },
    ],
  },
  {
    id: 'worldwide',
    emoji: '🌍',
    title: 'Global Citizen',
    requirement: 'Rate places in 20 different cities.',
    rarity: 2,
    criteria: [{ stat: 'citiesRated', threshold: 20 }],
  },
  {
    id: 'scene_veteran',
    emoji: '🌙',
    title: 'Night Owl',
    requirement: 'Rate 50 different venues.',
    rarity: 3,
    criteria: [{ stat: 'venuesRated', threshold: 50 }],
  },
  {
    id: 'traveler',
    emoji: '✈️',
    title: 'Frequent Flyer',
    requirement: 'Rate places in 10 different cities.',
    rarity: 4,
    criteria: [{ stat: 'citiesRated', threshold: 10 }],
  },
  {
    id: 'disco',
    emoji: '🪩',
    title: 'Clubber',
    requirement: 'Rate 20 different clubs.',
    rarity: 5,
    criteria: [{ stat: 'clubsRated', threshold: 20 }],
  },
  {
    id: 'bar_hopper',
    emoji: '🍻',
    title: 'Bar Hopper',
    requirement: 'Rate 20 different bars.',
    rarity: 6,
    criteria: [{ stat: 'barsRated', threshold: 20 }],
  },
  {
    id: 'night_owl',
    emoji: '🕺',
    title: 'Regular',
    requirement: 'Rate 10 different venues.',
    rarity: 7,
    criteria: [{ stat: 'venuesRated', threshold: 10 }],
  },
  {
    id: 'out_of_town',
    emoji: '🧳',
    title: 'Traveler',
    requirement: 'Rate places in 3 different cities.',
    rarity: 8,
    criteria: [{ stat: 'citiesRated', threshold: 3 }],
  },
  {
    id: 'first_round',
    emoji: '🥂',
    title: 'Cheers',
    requirement: 'Rate your first venue.',
    rarity: 9,
    criteria: [{ stat: 'venuesRated', threshold: 1 }],
  },
];
